import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetAllClients, useGetUnassignedUsers } from "../../queries/admin";
import AddCompanyModal from "../../components/modal/AddCompanyModal";
import AssignUserModal from "../../components/modal/AssignUserModal";
import { PlusIcon, BuildingOffice2Icon, UserPlusIcon } from "@heroicons/react/24/outline";

export default function ClientListPage() {
  const { data: clients, isLoading } = useGetAllClients();
  const { data: unassignedUsers } = useGetUnassignedUsers();
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState<{ id: string; name: string } | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Organizations
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {clients?.length ?? 0} organizations
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <PlusIcon className="h-4 w-4" />
            Add Client
          </button>
        </div>
      </div>

      {/* Unassigned Users Banner */}
      {unassignedUsers && unassignedUsers.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UserPlusIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {unassignedUsers.length} user{unassignedUsers.length !== 1 ? "s" : ""} without an organization
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Click "Assign" on any organization card below to add them
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {clients?.map((client) => (
          <div
            key={client.id}
            className="relative flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#111B21] p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <Link to={`/user/${client.id}`} className="flex-1">
              <div className="flex items-center gap-x-4 mb-4">
                {client.logo ? (
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <BuildingOffice2Icon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {client.name}
                  </h3>
                  {client.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {client.email}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span>{client.users?.length ?? 0} users</span>
                <span>{client.devices?.length ?? 0} devices</span>
              </div>
            </Link>

            {/* Assign users button — only show if there are unassigned users */}
            {unassignedUsers && unassignedUsers.length > 0 && (
              <button
                onClick={() => setAssignTarget({ id: client.id, name: client.name })}
                className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <UserPlusIcon className="h-3.5 w-3.5" />
                Assign Users
              </button>
            )}
          </div>
        ))}
      </div>

      {(!clients || clients.length === 0) && (
        <div className="text-center py-12">
          <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
            No organizations
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new organization.
          </p>
        </div>
      )}

      {showAddModal && (
        <AddCompanyModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {assignTarget && (
        <AssignUserModal
          isOpen={!!assignTarget}
          onClose={() => setAssignTarget(null)}
          clientId={assignTarget.id}
          clientName={assignTarget.name}
        />
      )}
    </div>
  );
}
